<template>  
  <div class="super-collection__collection">
    <div class="super-collection__collection-container">
      <div class="super-collection__collection-header">
        <div class="super-collection__collection-header-text">
          <h4>{{ settings.recommendationsHeading }}</h4>
        </div>
        <div class="controls-container u-hidden">
          <div class="arrows">
            <div class="arrow arrow-prev" @click="slidePrev">
              <icon fill-class="fill-black" name="chevron-left" size="16px"></icon>
            </div>
            <div class="arrow arrow-next" @click="slideNext">
              <icon fill-class="fill-black" name="chevron-right" size="16px"></icon>
            </div>
          </div>
        </div>
      </div>
      <div class="super-collection__collection-slider-container">
        <swiper :options="swiperWithScrollbar(`[data-scrollbar='super-collection-progress']`)" :ref="`superCollectionSwiper`">
          <swiper-slide class="super-collection__collection-slide" v-for="(product) in gridProducts" :key="product.id">
            <product-tile :settings="settings" class="super-collection__product-tile" :simple="true" :product="productWithoutVariants(product)" :truncateTitle="false" :fullTileClick="true">{{product.title}}</product-tile>
          </swiper-slide>
        </swiper>
        <div class="super-collection__progress-bar" data-scrollbar="super-collection-progress"></div>
        <div class="text-center super-collection__pagination u-hidden" slot="pagination" v-if="gridProducts.length > 1"></div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { swiper, swiperSlide } from 'vue-awesome-swiper'
import Icon from '@/scripts/components/basic/Icon.vue'
import ProductTile from '@/scripts/components/product/ProductTile.vue'

export default {
  name: 'RecommendedProducts',
  components: {
    swiper,
    swiperSlide,
    Icon,
    ProductTile
  },
  props: {
    products: Array,
    settings: Object,
    title: {
      type: String,
      default: 'You might like these too.'
    }
  },
  computed: {
    gridProducts () {
      return this.products.slice(0, 8)
    }
  },
  methods: {
    slidePrev (index) {
      this.$refs.superCollectionSwiper.swiper.slidePrev()
    },
    slideNext (index) {
      this.$refs.superCollectionSwiper.swiper.slideNext()
    },
    swiperWithScrollbar(el) {
      return { ...this.swiperOptions, scrollbar: { el, draggable: true } };
    },
    productWithoutVariants (product) {
      return { ...product, variants: [], options: [] }
    }
  },
  data () {
    return {
      swiperOptions: {
        slidesPerView: 3,
        spaceBetween: 40,
        pagination: {
          el: '.super-collection__pagination',
          clickable: true
        },
        threshold: 50,
        iOSEdgeSwipeDetection: true,
        iOSEdgeSwipeThreshold: 100,
        breakpoints: {
          768: {
            slidesPerView: 2,
            slidesPerGroup: 2,
            spaceBetween: 10,
          }
        }
      },
      collectionProducts: {}
    }
  }
}
</script>

<style scoped lang="scss">

  .controls-container {
    .arrows {
      display: flex;
      flex-direction: row;
      cursor: pointer;
      line-height: 0;
      .arrow-next,
      .arrow-prev {
        svg {
          transition: stroke 150ms ease-in;
          stroke-width: 1px;
          stroke: transparent;
        }
        &:hover {
          svg {
            stroke-width: 1px;
            stroke: $color-black;
          }
        }
      }
      .arrow-prev {
        padding-right: 32px;
        transform: rotate(180deg);
      }
      .arrow-next {
        padding-left: 32px;
      }
    }
  }

  /deep/ .swiper-pagination-bullets {
    line-height: 0;
    margin-top: 24px;
  }

  /deep/ .swiper-pagination-bullet {
    width: 5px;
    height: 5px;
    border: 1px solid $color-off-black;
    opacity: 1;
    background: transparent;
    margin: 0 1px;
  }

  /deep/ .swiper-pagination-bullet-active {
    background: $color-off-black;
  }

  .super-collection {
    &__collection-container {
      max-width: $container-max-width-slim;
      margin: 0 auto;
      padding: 0 15px;
      @include mobile-only {
        padding: 0;
      }
    }

    &__collection-container--with-slider {
      padding-bottom: 24px;

      &:nth-child(1) {
        @include tablet-up {
          padding-top: 24px;
        }
      }
    }

    &__collection {
      overflow: hidden;
    }

    &__collection-header {
      display: flex;
      margin-bottom: 20px;
      justify-content: space-between;
      align-items: flex-end;
    }

    &__collection-header-text {
      display: flex;
      width: 100%;
      align-items: center;
      justify-content: center;
      @include tablet-up {
        justify-content: flex-start;
      }
      h4 {
        @include mobile-only {
          @include h6;
        }
      }
    }

    &__collection-slider-container {
      position: relative;

      /deep/ .swiper-container {
        padding-right: 1px;
        overflow: visible;
        &:before {
          content: '';
          position: absolute;
          height: 100%;
          left: -3000px; // account for any gutter width
          width: 3000px; // account for any gutter width
          background-color: $color-white;
          z-index: 2;
        }
      }

      /deep/ .swiper-wrapper {
        padding: 0 1px 1px 0;
      }
    }

    &__collection-slide {
      height: auto;
    }

    &__progress-bar {
      margin: 16px auto 0;
      background-color: $color-light-grey;
      height: 2px;
      max-width: calc(100% - 60px);

      @include tablet-up {
        margin-bottom: 40px;
        max-width: 100%;
      }

      @include mobile-tablet {
        margin-top: 40px;
      }

      /deep/ .swiper-scrollbar-drag {
        height: 2px;
        margin-top: 16px;
        background-color: $color-brand-primary;
        border-radius: 0;

        @include tablet-up {
          margin-top: 44px;
        }
      }
    }

    &__collection-title {
      margin-bottom: 4px;

      @include mobile-only {
        @include h5-tablet;
      }
      @include tablet-up {
        margin-bottom: 0;
      }
    }

    /deep/ &__product-tile {
      height: auto;
      overflow: hidden;

      .super-collection__collection-grid & {
        margin: 0 12px 8px;
        flex-basis: calc(50% - 24px);

        @include tablet-up {
          margin: 0 12px 24px;
          flex-basis: calc(25% - 24px);
        }
        @include desktop-up {
          margin: 0 20px 24px;
          flex-basis: calc(25% - 40px);
        }
      }

      .super-collection__collection-slide & {
        height: 100%;
      }

      .product-tile__static-text-content {
        height: auto;
        border-top: 0;
      }
    }
  }
</style>
